'use client';

// react
import { ReactNode, useEffect, useRef } from 'react';

// hooks
import { useWindowSize } from '@/hooks/useWindowSize';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { breakpointValues } from '@/theme/breakpoint';
import { gridGap, sp } from '@/theme/mui';

/**
 * CardContainer is a container for multiple cards.
 */
export const CardContainer = ({
  children,
  maxColumns = 2,
  size,
  isPreventHeightMatch = false,
}: CardContainerProps) => {
  // state
  const currentWidth = useRef<number>(0);

  // refs
  const styledCardContainerRef = useRef<HTMLDivElement>(null);

  // hooks
  const [width] = useWindowSize();

  if (width > 0 && width < breakpointValues.smValue) {
    maxColumns = 1;
  }

  // Track child count to avoid re-running on every render (children reference changes each time)
  const childCount = Array.isArray(children)
    ? children.length
    : children
      ? 1
      : 0;

  // effects
  useEffect(() => {
    if (isPreventHeightMatch) return;

    const matchHeights = () => {
      if (!styledCardContainerRef.current) return;

      if (currentWidth.current !== width) {
        const cards = styledCardContainerRef.current.children;
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i] as HTMLElement;
          card.style.height = '';
        }
      }

      const cards = styledCardContainerRef.current.children;
      let maxHeight = 0;
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i] as HTMLElement;
        maxHeight = Math.max(maxHeight, card.offsetHeight);
      }
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i] as HTMLElement;
        card.style.height = `${maxHeight}px`;
      }
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(matchHeights);
    } else {
      matchHeights();
    }
  }, [isPreventHeightMatch, width, childCount]);

  return maxColumns === 1 ? (
    <Stack sx={{ gap: sp[16], width: '100%' }}>{children}</Stack>
  ) : (
    <Box
      ref={styledCardContainerRef}
      sx={{
        width: '100%',
        display: 'grid',
        gap: gridGap[size || 'default'],
        gridTemplateColumns: `repeat(${maxColumns}, 1fr)`,
        '& > *': { width: '100%' },
      }}
    >
      {children}
    </Box>
  );
};

interface CardContainerProps {
  children?: ReactNode;
  maxColumns?: number;
  isPreventHeightMatch?: boolean;
  size?: string;
}

CardContainer.displayName = 'CardContainer';

export default CardContainer;
